import moment from 'moment-timezone'

import { Periods, SchedulingMethods } from 'constants/meetings'
import { isValidSupergraphScopeType } from 'features/meetings/supergraph/scope_utils'
import { STATUS_MAP } from 'services/search_api/constants'

/**
 * Non-organizer scope + notetaker-ineligible externals — same policy as {@link buildFilterString}
 * (`buildIsOrganizerFilter` / `buildIncludeNotetakerIneligibleExternalEventsFilter`).
 *
 * @param {Record<string, unknown>} filter - Mutated in place.
 */
export const applyFixedMeetingsOrganizerScope = (filter) => {
  filter.is_organizer = false
  filter.include_notetaker_ineligible_external_events = true
}

/**
 * Time window for SuperGraph list filters — mirrors `buildPeriodFilter` / `addDateRangeFilter`
 * in `services/search_api/utils.js` (publisher GraphQL) so search, date presets, and
 * upcoming (future | today | past) legs match the legacy behavior.
 *
 * @param {object | null} uiFilters
 * @param {{ today?: boolean, past?: boolean, forCount?: boolean }} options
 * @returns {{ operation: string, start: string, end?: string } | null}
 */
export const buildSupergraphTimeFilter = (uiFilters, { today, past, forCount }) => {
  if (!uiFilters) return null
  const { period, startDate, endDate } = uiFilters

  const addDateRangeFilterForCount = (rangeStart, rangeEnd) => ({
    operation: 'BETWEEN',
    start: moment(rangeStart).format('YYYY-MM-DDTHH:mm:ssZ'),
    end: moment(rangeEnd).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
  })

  if (forCount) {
    switch (period) {
      case Periods.UPCOMING:
        return null
      case Periods.FIXED:
        if (!startDate || !endDate) return null
        return addDateRangeFilterForCount(startDate, endDate)
      case Periods.THIS_WEEK:
        return addDateRangeFilterForCount(moment().startOf('week'), moment().endOf('week'))
      case Periods.THIS_MONTH:
        return addDateRangeFilterForCount(moment().startOf('month'), moment().endOf('month'))
      case Periods.THIS_YEAR:
        return addDateRangeFilterForCount(moment().startOf('year'), moment().endOf('year'))
      case Periods.TODAY:
        return addDateRangeFilterForCount(moment().startOf('day'), moment().endOf('day'))
      default:
        return null
    }
  }

  const todayStart = moment().startOf('day')
  const startOfTodayStr = todayStart.format('YYYY-MM-DDTHH:mm:ssZ')
  const endOfTodayStr = moment().endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')

  const addDateRangeFilter = (rangeStart, rangeEnd) => {
    const formattedStartDate = moment(rangeStart).format('YYYY-MM-DDTHH:mm:ssZ')
    const formattedEndDate = moment(rangeEnd).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')
    if (past) {
      const actualEndDate = moment(rangeEnd).isBefore(todayStart)
        ? formattedEndDate
        : endOfTodayStr
      return {
        operation: 'BETWEEN',
        start: formattedStartDate,
        end: actualEndDate,
      }
    }
    const actualStartDate = moment(rangeStart).isAfter(todayStart)
      ? formattedStartDate
      : startOfTodayStr
    return {
      operation: 'BETWEEN',
      start: actualStartDate,
      end: formattedEndDate,
    }
  }

  switch (period) {
    case Periods.UPCOMING: {
      if (today) {
        return {
          operation: 'BETWEEN',
          start: startOfTodayStr,
          end: endOfTodayStr,
        }
      }
      if (past) {
        return { operation: 'LT', start: startOfTodayStr }
      }
      return { operation: 'GTE', start: endOfTodayStr }
    }
    case Periods.FIXED:
      if (!startDate || !endDate) return null
      return addDateRangeFilter(startDate, endDate)
    case Periods.THIS_WEEK:
      return addDateRangeFilter(moment().startOf('week'), moment().endOf('week'))
    case Periods.THIS_MONTH:
      return addDateRangeFilter(moment().startOf('month'), moment().endOf('month'))
    case Periods.THIS_YEAR:
      return addDateRangeFilter(moment().startOf('year'), moment().endOf('year'))
    case Periods.TODAY:
      return addDateRangeFilter(moment().startOf('day'), moment().endOf('day'))
    default:
      return null
  }
}

/**
 * Map UI/search-api-style filters -> Meetings_MeetingsFilter for SuperGraph.
 *
 * @param {object | null} uiFilters
 * @param {{ today?: boolean, past?: boolean, forCount?: boolean }} options
 * @returns {object | null}
 */
export const buildSupergraphFilterFromUiFilters = (uiFilters, { today, past, forCount } = {}) => {
  if (!uiFilters) return null

  const {
    inviteeEmails,
    eventTypeIds,
    userIds,
    teamIds,
    role,
    ownerType,
    searchKeyword,
    meetingIntelligenceStatus,
    statusIds,
    schedulingMethods,
    scopeType,
    scopeId,
    uuid,
  } = uiFilters

  const filter = {}

  if (inviteeEmails?.length) filter.invitee_emails = inviteeEmails
  if (eventTypeIds?.length) filter.event_type_uuids = eventTypeIds
  if (userIds?.length) filter.user_uuids = userIds
  if (teamIds?.length) filter.team_uuids = teamIds

  if (role) filter.role = role
  if (ownerType) filter.owner_type = ownerType

  if (meetingIntelligenceStatus) {
    filter.meeting_intelligence_status = meetingIntelligenceStatus
  }
  if (searchKeyword) filter.search_keyword = searchKeyword

  if (isValidSupergraphScopeType(scopeType)) {
    filter.scope_type = scopeType
  }
  if (scopeId) filter.scope_id = scopeId

  if (uuid) filter.ids = [uuid]

  if (schedulingMethods?.length) {
    const kinds = []
    if (schedulingMethods.includes(SchedulingMethods.CALENDLY)) kinds.push('CALENDLY')
    if (schedulingMethods.includes(SchedulingMethods.GOOGLE)) kinds.push('GOOGLE')
    if (schedulingMethods.includes(SchedulingMethods.OUTLOOK)) kinds.push('OUTLOOK')
    if (kinds.length) filter.kinds = kinds
  }

  if (statusIds?.length) {
    const mapped = statusIds
      .map((id) => STATUS_MAP[id])
      .filter(Boolean)

    if (mapped.length === 1) {
      filter.status = mapped[0]
    } else if (mapped.length > 1) {
      filter.statuses = mapped
    }
  }

  const timeFilter = buildSupergraphTimeFilter(uiFilters, { today, past, forCount })
  if (timeFilter) filter.start_time = timeFilter

  applyFixedMeetingsOrganizerScope(filter)
  return filter
}

/**
 * SuperGraph total count for the meetings filter bar — mirrors legacy `getMeetingsFilterCount`
 * (`buildFilterString` with `forCount: true`, including no `start_time` for upcoming).
 */
export const buildSupergraphCountInput = (uiFilters) => {
  const filter = buildSupergraphFilterFromUiFilters(uiFilters, { forCount: true })
  return {
    filter,
    pagination: {
      first: 1,
      after: null,
    },
    orderBy: {
      start_time: 'ASC',
    },
  }
}

/**
 * Build full Meetings_MeetingsInput for SuperGraph.
 *
 * @param {object | null} uiFilters
 * @param {{ today?: boolean, past?: boolean, first: number, after?: string | null, order: string }} options
 */
export const buildSupergraphInput = (uiFilters, { today, past, first, after, order }) => {
  const filter = buildSupergraphFilterFromUiFilters(uiFilters, { today, past })
  return {
    filter,
    pagination: {
      first,
      after: after || null,
    },
    orderBy: {
      start_time: order,
    },
  }
}

/**
 * True when lazy SuperGraph `originalArgs` use the same fixed-period `start_time` window as
 * `visibleRangeUiVars`. Mirrors legacy checks on `originalArgs.startDate` / `endDate` so a
 * next-page response from an old calendar range is ignored after the user scrolls.
 *
 * @param {object | undefined | null} originalArgs
 * @param {object | null | undefined} visibleRangeUiVars
 */
export const doesSupergraphLazyArgsMatchFixedVisibleRange = (originalArgs, visibleRangeUiVars) => {
  if (!visibleRangeUiVars || visibleRangeUiVars.period !== Periods.FIXED) return false
  const expected = buildSupergraphFilterFromUiFilters(visibleRangeUiVars, {})
  const lazyPageStartTimeFilter = originalArgs?.filter?.start_time
  const visibleRangeStartTimeFilter = expected?.start_time
  if (!lazyPageStartTimeFilter || !visibleRangeStartTimeFilter) return false
  return lazyPageStartTimeFilter.operation === visibleRangeStartTimeFilter.operation
    && lazyPageStartTimeFilter.start === visibleRangeStartTimeFilter.start
    && lazyPageStartTimeFilter.end === visibleRangeStartTimeFilter.end
}

/**
 * Whether lazy pagination args represent "load older" (past / DESC) vs "load more future".
 *
 * @param {object | undefined | null} args
 * @param {boolean} isSupergraphEnabled
 */
export const isPreviousPageLazyArgs = (args, isSupergraphEnabled) => {
  if (!args) return false
  if (isSupergraphEnabled) return args.orderBy?.start_time === 'DESC'
  return Boolean(args.isPrevious || args.past)
}
